







































import { Component, Prop, Vue, Watch, Inject } from "vue-property-decorator";
import { getModule } from "vuex-module-decorators";

import FullscreenGallery from "@/modules/plushie/components/FullscreenGallery.vue";

import preloadImage from "@/lib/preload-image.function";
import ErrorConverterService from "@/modules/forms/error-converter.service";
import ImageHandlerService from "@/modules/file-storage/image-handler.service";
import dataStore from "@/store";
import FileStorageStore from "@/modules/file-storage/store";
import VideoHandlerService from "@/modules/file-storage/video-handler.service";

import PlushieStore from "../store";
import QaAsset from "../qa-asset.model";
import QualityInspectionStore from "../store";
import isAssetVideo from "../is-asset-video.function";
import QaAssetType from "../qa-asset-type";

@Component({
  components: {
    FullscreenGallery,
  },
})
export default class FinishedProductPhotosGallery extends Vue {
  @Prop({ required: true })
  public readonly plushieId!: string;

  @Prop({ default: true })
  public readonly isReadOnly!: boolean;

  @Inject("ErrorConverterService")
  private errorConverterService!: ErrorConverterService;

  @Inject("ImageHandlerService")
  private imageHandlerService!: ImageHandlerService;

  @Inject("VideoHandlerService")
  private fVideoHandlerService!: VideoHandlerService;

  @Inject("window")
  private fWindow!: Window;

  private fIsLoading = true;
  private fIsDisabled = false;

  private fCurrentIndex = 0;

  private fFileStorageDataStore: FileStorageStore;
  private fPlushieDataStore: PlushieStore;
  private fQualityInspectionDataStore: QualityInspectionStore;

  get assets(): QaAsset[] {
    return this.fQualityInspectionDataStore.getLastGroupQaAssetsByPlushieId(
      this.plushieId
    );
  }

  get currentIndex() {
    return this.fCurrentIndex;
  }

  get isLoading(): boolean {
    return this.fIsLoading;
  }

  get isDisabled(): boolean {
    return this.fIsDisabled;
  }

  get isTouchDevice(): boolean {
    return this.fWindow.matchMedia("(pointer: coarse)").matches;
  }

  get stageImage(): QaAsset | undefined {
    if (this.currentIndex == null) {
      return undefined;
    }

    return this.assets[this.currentIndex];
  }

  public constructor() {
    super();
    this.fFileStorageDataStore = getModule(FileStorageStore, dataStore);
    this.fPlushieDataStore = getModule(PlushieStore, dataStore);
    this.fQualityInspectionDataStore = getModule(
      QualityInspectionStore,
      dataStore
    );
  }

  public getStageThumbnail(asset: QaAsset) {
    return this.getThumbnail(asset, 600);
  }

  public getFullscreenThumbnail(asset: QaAsset) {
    return this.getThumbnail(asset, 1920);
  }

  public getThumbnail(asset: QaAsset, size: number, crop = false) {
    const storageItemId = isAssetVideo(asset)
      ? asset.thumbnailStorageItem
      : asset.storageItem;

    const storageItem = this.fFileStorageDataStore.getItemById(storageItemId);

    const url = storageItem
      ? storageItem.url
      : this.fFileStorageDataStore.placeholderUrl;

    return this.imageHandlerService.getThumbnailUrl(url, size, size, crop);
  }

  public isVideoAsset(qaAsset: QaAsset): boolean {
    return qaAsset.type === QaAssetType.VIDEO;
  }

  public launchGalleria() {
    const gallery = this.getFullscreenGallery();
    if (!gallery || this.currentIndex == null) {
      return;
    }

    const slidesData = this.assets.map((asset) => {
      if (isAssetVideo(asset)) {
        const storageItem = this.fFileStorageDataStore.getItemById(
          asset.storageItem
        );

        return {
          href: this.fVideoHandlerService.getOriginalVideoUrl(
            storageItem
              ? storageItem.url
              : this.fFileStorageDataStore.placeholderUrl
          ),
          thumbnail: this.getThumbnail(asset, 200, true),
          poster: this.getFullscreenThumbnail(asset),
          type: "video/mp4",
        };
      } else {
        return {
          href: this.getFullscreenThumbnail(asset),
          thumbnail: this.getThumbnail(asset, 200, true),
        };
      }
    });

    gallery.launchGalleria(slidesData, this.currentIndex);
  }

  public setCurrentIndex(index: number) {
    this.fCurrentIndex = index;
  }

  private getFullscreenGallery(): FullscreenGallery | undefined {
    return this.$refs["fullscreen-gallery"] as FullscreenGallery;
  }

  private async getPlushieData(plushieId: string) {
    await this.fQualityInspectionDataStore.loadQaAssetsByPlushieId({
      plushieId: plushieId,
    });

    const qaAssets = this.fQualityInspectionDataStore.getLastGroupQaAssetsByPlushieId(
      plushieId
    );

    const qaAssetsStorageItemsIds: string[] = [];
    qaAssets.forEach((qaAsset) => {
      qaAssetsStorageItemsIds.push(qaAsset.storageItem);
      if (isAssetVideo(qaAsset)) {
        qaAssetsStorageItemsIds.push(qaAsset.thumbnailStorageItem);
      }
    });

    await this.fFileStorageDataStore.loadItemsByIds(qaAssetsStorageItemsIds);

    this.$nextTick(() => {
      void this.preloadImages();
    });
  }

  private async preloadImages() {
    await Promise.all(
      this.assets.map((photo) => preloadImage(this.getStageThumbnail(photo)))
    );

    await Promise.all(
      this.assets.map((photo) =>
        preloadImage(this.getFullscreenThumbnail(photo))
      )
    );
  }

  @Watch("assets", { immediate: false })
  private async onAssetsChange() {
    if (this.isLoading) {
      return;
    }

    await this.preloadImages();
  }

  @Watch("plushieId", { immediate: true })
  private async onPlushieIdChange() {
    if (!this.plushieId) {
      return;
    }

    this.fIsLoading = true;

    await this.getPlushieData(this.plushieId);

    this.fIsLoading = false;
  }
}
